import React from "react";
import { Link } from "react-router-dom";

const JournalAbout = ({ publication, latestYear, parentDetails, pubtype, activeTab, availableLanguages, setActiveTab, webAddress, navigate, accessType }) => {

  const pickAttr = (pub, name) => {
    if (!pub) return null;
    const from = (arr = []) => arr.find(a => a.attributename === name)?.value || [];
    const all = [...from(pub.templateInfo), ...from(pub.publicationtypeInfo)];
    const byLang = (lang) => all.find(v => v?.lang === lang)?.value?.[0];
    return (byLang && (byLang(activeTab) || byLang("tr"))) ?? (all[0]?.value?.[0] ?? null);
  };
  // Belirli bir attributename ve dil için güvenli veri çekme fonksiyonu
  const getSpecificAttr = (source, attrName, lang = "tr") => {
    return publication?.[source]
      ?.find((attr) => attr.attributename === attrName)
      ?.value.find((val) => val.lang === lang)?.value[0];
  };

  // Sizin istediğiniz hiyerarşi: Dergi Hakkında > Açıklama > Özet
  const getMainContent = () => {
    // 1. Dergi Hakkında (templateInfo içinden)
    const journalAbout = getSpecificAttr("templateInfo", "Dergi Hakkında", "tr");
    if (journalAbout) return journalAbout;

    // 2. Açıklama (publicationtypeInfo içinden)
    const description = getSpecificAttr("publicationtypeInfo", "Açıklama", "tr") ||
      getSpecificAttr("publicationtypeInfo", "Açıklama", "en");
    if (description) return description;

    // 3. Özet (Önce template sonra publicationtype bakılır)
    const summary = getSpecificAttr("templateInfo", "Özet", "tr") ||
      getSpecificAttr("publicationtypeInfo", "Özet", "tr");

    return summary || ""; // Hiçbiri yoksa boş dön
  };

  const getVal = (name) => {
    const fromArr = (arr = []) => arr.find(a => a.attributename === name)?.value || [];
    const all = [
      ...fromArr(publication?.templateInfo),
      ...fromArr(publication?.publicationtypeInfo),
    ];
    const byLang = all.find(v => v?.lang === activeTab)?.value?.[0];
    return byLang ?? all[0]?.value?.[0] ?? null;
  };

  const stripHtml = (html) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || "";
  };

  const countryVal = getVal("Ülke");
  const lastDate = getVal("Yayın Tarihi") || getVal("Son Yayınlandığı Tarih");
  const lastPubDate = latestYear || lastDate?.substring(0, 4);
  const periodVal =
    (publication?.period && publication.period !== "0" && publication.period) ||
    getVal("Periyot") ||
    (publication?.parentList?.[0]?.period ?? null);
  const periodNumber = (publication?.periodnumber && publication.periodnumber !== "0" && publication.periodnumber) ||
    getVal("periodnumber") ||
    (publication?.parentList?.[0]?.periodnumber ?? null);

  const issnVal = pickAttr(publication, "ISSN");
  const eIssnVal = pickAttr(publication, "E-ISSN");

  const extractAttrArr = (list, attrName) => {
    if (!Array.isArray(list)) return [];
    const attr = list.find((a) => a.attributename === attrName);
    if (!attr || !attr.value) return [];
    if (Array.isArray(attr.value)) {
      const trVal = attr.value.find((v) => v.lang === "tr" && Array.isArray(v.value))?.value;
      if (trVal) return trVal;
      const firstVal = attr.value.find((v) => Array.isArray(v.value))?.value;
      if (firstVal) return firstVal;
      if (typeof attr.value[0] === "string") return attr.value;
    }
    return [];
  };

  const allKeywords = React.useMemo(() => {
    const pubTypeKeywords = extractAttrArr(publication?.publicationtypeInfo, "Anahtar Kelimeler");
    const pubTypeTopics = extractAttrArr(publication?.publicationtypeInfo, "Konu Başlıkları");
    const templateTopics = extractAttrArr(publication?.templateInfo, "Konu Başlıkları");
    const templateKeywords = extractAttrArr(publication?.templateInfo, "Anahtar Kelimeler");

    const combined = [
      ...pubTypeKeywords,
      ...pubTypeTopics,
      ...templateTopics,
      ...templateKeywords,
    ]
      .map((x) => (typeof x === "string" ? x.trim() : String(x).trim()))
      .filter(Boolean);

    return Array.from(new Set(combined));
  }, [publication]);

  return (
    <div className="card-body mt-0 mb-0">
      {/* Başlık (BookAbout ile aynı stil) */}
      <h5 className="fw-semibold text-darkfume fs-6 mt-0 mb-2">
        {pubtype} Tanıtım Yazısı:
      </h5>

      {/* Özet */}
      <div className="tab-content mt-2">
        <ul>
          <li>
            <div className="text-darkfume">
              {/* Fallback mantığıyla tek bir içerik basıyoruz */}
              {stripHtml(getMainContent()) || "Tanıtım yazısı bulunamadı."}
            </div>
          </li>

          {/* Anahtar Kelimeler — BookAbout’a paralel tipografi */}
          {allKeywords.length > 0 && (
            <ul>
              <li className="text-darkfume">
                <b style={{ fontSize: "0.92em" }}>Anahtar Kelimeler: </b>
                {allKeywords.map((keyword, index) => (
                  <span
                    key={index}
                    style={{ fontSize: "0.92em", cursor: "pointer", color: "#392C7D" }}
                    onClick={() => {
                      navigate(`/publication-list?keywords=${encodeURIComponent(keyword)}`);
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.textDecoration = "underline")}
                    onMouseOut={(e) => (e.currentTarget.style.textDecoration = "none")}
                  >
                    {index !== 0 && ", "} {keyword}
                  </span>
                ))}
              </li>
            </ul>
          )}
        </ul>
      </div>

      {/* Dergi Editörleri */}
      <hr className="my-3 text-darkfume" />
      <div>
        <h5 className="fw-semibold mb-1 mt-3 text-darkfume fs-6" style={{ marginBottom: 0 }}>
          Dergi Editörleri
        </h5>
        <div className="card-body">
          {publication?.authors && publication.authors.length > 0 ? (
            <div className="text-darkfume">
              {publication.authors.map((author, idx) => (
                <div
                  key={author.mysqlId || idx}
                  style={{ display: "flex", alignItems: "center", marginBottom: 2 }}
                >
                  <span style={{ marginRight: 6 }}>-</span>
                  <Link
                    to={`/authors-detail/${author.mysqlId}`}
                    style={{ textDecoration: "none", color: "#392C7D" }}
                    onMouseOver={(e) => (e.currentTarget.style.textDecoration = "underline")}
                    onMouseOut={(e) => (e.currentTarget.style.textDecoration = "none")}
                  >
                    {author.name} {author.surname}
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <span style={{ fontSize: "0.93em" }} className="text-darkfume">
              Editör bilgisi bulunamadı.
            </span>
          )}
        </div>
      </div>

      {/* Web Adresi */}
      {webAddress && (
        <>
          <hr className="my-3 text-darkfume" />
          <div>
            <h5 className="fw-semibold mb-1 mt-1 text-darkfume fs-6" style={{ marginBottom: 0 }}>
              Web Adresi
            </h5>
            <div className="card-body">
              <a
                href={webAddress}
                target="_blank"
                rel="noopener noreferrer"
                className="border-0 bg-transparent"
                style={{ textDecoration: "none", color: "#392C7D" }}
                onMouseOver={(e) => (e.currentTarget.style.textDecoration = "underline")}
                onMouseOut={(e) => (e.currentTarget.style.textDecoration = "none")}
              >
                {webAddress}
              </a>
            </div>
          </div>
        </>
      )}

      {/* Teknik Özellikler */}
      <hr className="my-3 text-darkfume" />
      <div>
        <h5 className="fw-semibold mb-1 mt-1 text-darkfume fs-6" style={{ marginBottom: 0 }}>
          Teknik Özellikler
        </h5>
        <div className="card-body text-darkfume">
          {pubtype !== "Dergi" && (
            <div>
              <div style={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
                <span style={{ marginRight: 6, fontWeight: "bold", color: "#1d4ed8" }}>-</span>
                <span className="fw-bold" style={{ marginRight: 4 }}>Edisyon Bilgileri:</span>
                <span>{parentDetails.year || "-"}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
                <span style={{ marginRight: 6, fontWeight: "bold", color: "#1d4ed8" }}>-</span>
                <span className="fw-bold" style={{ marginRight: 4 }}>Cilt:</span>
                <span style={{ marginRight: 8 }}>{parentDetails.volume || "-"}</span>
                <span className="fw-bold" style={{ marginRight: 4 }}>Sayı:</span>
                <span>{parentDetails.issue || "-"}</span>
              </div>
            </div>
          )}

          {pubtype === "Dergi" && (
            <div>
              {getVal("Yayın Yeri") && (
                <div style={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
                  <span style={{ marginRight: 6, fontWeight: "bold", color: "#1d4ed8" }}>-</span>
                  <span className="fw-bold" style={{ marginRight: 4 }}>Yayın Yeri:</span>
                  <span>{getVal("Yayın Yeri")}</span>
                </div>
              )}

              {countryVal && (
                <div style={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
                  <span style={{ marginRight: 6, fontWeight: "bold", color: "#1d4ed8" }}>-</span>
                  <span className="fw-bold" style={{ marginRight: 4 }}>Ülke:</span>
                  <span>{countryVal}</span>
                </div>
              )}

              {lastPubDate && (
                <div style={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
                  <span style={{ marginRight: 6, fontWeight: "bold", color: "#1d4ed8" }}>-</span>
                  <span className="fw-bold" style={{ marginRight: 4 }}>Son Yayınlandığı Tarih:</span>
                  <span>{lastPubDate}</span>
                </div>
              )}

              {periodVal && (
                <div style={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
                  <span style={{ marginRight: 6, fontWeight: "bold", color: "#1d4ed8" }}>-</span>
                  <span className="fw-bold" style={{ marginRight: 4 }}>Periyot:</span>
                  <span>{periodNumber} {periodVal}</span>
                </div>
              )}

              {issnVal && (
                <div style={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
                  <span style={{ marginRight: 6, fontWeight: "bold", color: "#1d4ed8" }}>-</span>
                  <span className="fw-bold" style={{ marginRight: 4 }}>ISSN:</span>
                  <span>{issnVal}</span>
                </div>
              )}

              {eIssnVal && (
                <div style={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
                  <span style={{ marginRight: 6, fontWeight: "bold", color: "#1d4ed8" }}>-</span>
                  <span className="fw-bold" style={{ marginRight: 4 }}>E-ISSN:</span>
                  <span>{eIssnVal}</span>
                </div>
              )}

              {accessType && (
                <div style={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
                  <span style={{ marginRight: 6, fontWeight: "bold", color: "#1d4ed8" }}>-</span>
                  <span className="fw-bold" style={{ marginRight: 4 }}>Erişim Türü:</span>
                  <span>{accessType}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Dosya Formatı */}
      <hr className="my-3 text-darkfume" />
      <div>
        <h5 className="fw-semibold mb-1 mt-1 text-darkfume fs-6" style={{ marginBottom: 0 }}>
          Dosya Formatı
        </h5>
        <div className="card-body text-darkfume">
          {publication?.pdffile
            ? publication.pdffile.split(".").pop()
            : "Dosya formatı bulunamadı"}
        </div>
      </div>
    </div>
  );
};

export default JournalAbout;
